import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus, ImageIcon, Info, Repeat, Send, X, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { eventsAPI } from '../api';

const CATEGORIES = ['general', 'music', 'family', 'sport', 'food', 'community', 'festival', 'theatre', 'market', 'charity'];
const ARRANGEMENT_THRESHOLD = 5;

const EMPTY = {
  title: '', description: '', start_date: '', end_date: '',
  venue: '', address: '', category: 'general', link: '', ticket_price: '0',
  submitter_name: '', submitter_email: '', submitter_org: '', submitter_phone: '',
  recurrence_mode: 'none', recurrence_count: '4', recurrence_dates: '',
  website: '', // honeypot
};

const SubmitEventPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const priceNum = Number(form.ticket_price || 0);
  const showArrangementNotice = priceNum > ARRANGEMENT_THRESHOLD;

  const onImage = (file) => {
    if (!file) { setImage(null); setImagePreview(''); return; }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Image too large', description: 'Please upload an image under 5MB', variant: 'destructive' });
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast({ title: 'Wrong format', description: 'JPEG, PNG or WebP only', variant: 'destructive' });
      return;
    }
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (image) fd.append('image', image);
      await eventsAPI.submitCommunity(fd);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast({
        title: 'Submission failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="submit-event-success">
        <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
          <Send className="text-emerald-600" size={28} />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-3">Thanks — nearly there!</h1>
        <p className="text-lg text-slate-600 mb-2">Check your inbox at <strong>{form.submitter_email}</strong>.</p>
        <p className="text-slate-600 mb-8">Click the confirmation link and we&apos;ll review your event within 24 hours.</p>
        {showArrangementNotice && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left mb-6" data-testid="submit-arrangement-notice-final">
            <p className="font-semibold text-amber-900 flex items-center gap-2"><Info size={16} /> A quick note about paid events</p>
            <p className="text-sm text-amber-800 mt-1">
              Because your ticket price is over £{ARRANGEMENT_THRESHOLD}/person, our site owner will be in touch directly before your event goes live — this is standard for paid events on paisley.org.uk.
            </p>
          </div>
        )}
        <Link to="/events" className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-semibold">← Back to the events calendar</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="submit-event-page">
      <div className="mb-8">
        <Link to="/events" className="text-sm text-slate-500 hover:text-slate-700">← Back to events</Link>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 flex items-center gap-3">
          <CalendarPlus className="text-sky-500" size={30} /> List your community event
        </h1>
        <p className="text-slate-600 mt-2">
          Free for community groups, charities, clubs and independent organisers.
          Fill in the form — we&apos;ll email you a confirmation link — then our editorial team publishes it within 24 hours.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
        {/* Honeypot — hidden from humans */}
        <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
          <label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)} /></label>
        </div>

        <section className="space-y-4">
          <h2 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2">About your event</h2>

          <div>
            <Label htmlFor="title">Event title *</Label>
            <Input id="title" required maxLength={200} value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Paisley Halloween Parade" data-testid="submit-title-input" />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Tell locals what to expect. Bullet points and short paragraphs work well." data-testid="submit-description-input" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Starts *</Label>
              <Input id="start_date" type="datetime-local" required value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="submit-start-input" />
            </div>
            <div>
              <Label htmlFor="end_date">Ends (optional)</Label>
              <Input id="end_date" type="datetime-local" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} data-testid="submit-end-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="venue">Venue *</Label>
              <Input id="venue" required value={form.venue} onChange={(e) => set('venue', e.target.value)} placeholder="Paisley Abbey" data-testid="submit-venue-input" />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input id="address" value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Abbey Close, Paisley, PA1 1JG" data-testid="submit-address-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger data-testid="submit-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="link">Booking / info link</Label>
              <Input id="link" type="url" value={form.link} onChange={(e) => set('link', e.target.value)} placeholder="https://…" data-testid="submit-link-input" />
            </div>
          </div>

          <div>
            <Label htmlFor="ticket_price">Ticket price per person (£)</Label>
            <Input id="ticket_price" type="number" min="0" step="0.5" value={form.ticket_price} onChange={(e) => set('ticket_price', e.target.value)} data-testid="submit-price-input" />
            <p className="text-xs text-slate-500 mt-1">Enter 0 for free events. See notice below for paid events.</p>
            {showArrangementNotice && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="submit-arrangement-notice">
                <p className="font-semibold flex items-center gap-2"><Info size={14} /> Paid event over £{ARRANGEMENT_THRESHOLD}/person</p>
                <p className="text-xs mt-1 text-amber-800">
                  Events with tickets over £{ARRANGEMENT_THRESHOLD}/person are subject to a commercial arrangement with paisley.org.uk. Our site owner will contact you after you verify your email — please don&apos;t publicise the event as being on paisley.org.uk until you&apos;ve heard back.
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2 flex items-center gap-2">
            <ImageIcon size={18} /> Image (optional)
          </h2>
          {imagePreview ? (
            <div className="relative inline-block">
              <img src={imagePreview} alt="Preview" className="max-h-48 rounded-lg border border-slate-200" data-testid="submit-image-preview" />
              <button type="button" onClick={() => onImage(null)} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-slate-800 text-white grid place-items-center hover:bg-slate-700" data-testid="submit-image-remove"><X size={14} /></button>
            </div>
          ) : (
            <label className="block border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-sky-400 hover:bg-sky-50/30 transition" data-testid="submit-image-drop">
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onImage(e.target.files?.[0])} />
              <ImageIcon className="mx-auto text-slate-400 mb-2" size={28} />
              <p className="text-sm font-semibold text-slate-700">Click to upload or drag & drop</p>
              <p className="text-xs text-slate-500 mt-1">JPEG, PNG or WebP · 5MB max</p>
            </label>
          )}
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2 flex items-center gap-2">
            <Repeat size={18} /> Does it repeat?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Cadence</Label>
              <Select value={form.recurrence_mode} onValueChange={(v) => set('recurrence_mode', v)}>
                <SelectTrigger data-testid="submit-recur-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">One-off (doesn&apos;t repeat)</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="custom">Specific dates</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {['weekly', 'fortnightly', 'monthly'].includes(form.recurrence_mode) && (
              <div>
                <Label htmlFor="recurrence_count">How many occurrences (2–52)</Label>
                <Input id="recurrence_count" type="number" min="2" max="52" value={form.recurrence_count} onChange={(e) => set('recurrence_count', e.target.value)} data-testid="submit-recur-count-input" />
              </div>
            )}
          </div>
          {form.recurrence_mode === 'custom' && (
            <div>
              <Label htmlFor="recurrence_dates">Dates (one per line, YYYY-MM-DD)</Label>
              <Textarea id="recurrence_dates" rows={4} value={form.recurrence_dates} onChange={(e) => set('recurrence_dates', e.target.value)} placeholder={"2026-08-01\n2026-08-15\n2026-09-05"} data-testid="submit-recur-dates-input" />
              <p className="text-xs text-slate-500 mt-1">Time inherits from the Starts field above.</p>
            </div>
          )}
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2">Your details</h2>
          <p className="text-xs text-slate-500 -mt-2">We only use this to email your confirmation link and follow up if needed. Never shown publicly.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="submitter_name">Your name *</Label>
              <Input id="submitter_name" required value={form.submitter_name} onChange={(e) => set('submitter_name', e.target.value)} data-testid="submit-name-input" />
            </div>
            <div>
              <Label htmlFor="submitter_email">Your email *</Label>
              <Input id="submitter_email" type="email" required value={form.submitter_email} onChange={(e) => set('submitter_email', e.target.value)} data-testid="submit-email-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="submitter_org">Organisation / group *</Label>
              <Input id="submitter_org" required value={form.submitter_org} onChange={(e) => set('submitter_org', e.target.value)} placeholder="Paisley Community Trust" data-testid="submit-org-input" />
            </div>
            <div>
              <Label htmlFor="submitter_phone">Phone (optional)</Label>
              <Input id="submitter_phone" type="tel" value={form.submitter_phone} onChange={(e) => set('submitter_phone', e.target.value)} data-testid="submit-phone-input" />
            </div>
          </div>
        </section>

        <div className="pt-2">
          <Button type="submit" disabled={busy} className="w-full h-12 bg-sky-500 hover:bg-sky-600 text-base font-semibold" data-testid="submit-event-btn">
            {busy ? 'Submitting…' : (<><Send size={16} className="mr-2" /> Submit event for review</>)}
          </Button>
          <p className="text-xs text-slate-500 mt-3 flex items-center justify-center gap-1"><Shield size={12} /> We&apos;ll email you a confirmation link — click it to complete your submission.</p>
        </div>
      </form>
    </div>
  );
};

export default SubmitEventPage;
